import { useEffect, useId, useState, type ChangeEvent } from 'react';
import { extractPtDigits, formatPtDigits, toCanonicalPhone } from '../../lib/phone';
import { FlagPT } from './FlagPT';
import styles from './PhoneInput.module.scss';

interface PhoneInputProps {
  value: string;
  onChange: (canonical: string) => void;
  autoComplete?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  className?: string;
  invalid?: boolean;
  onBlur?: () => void;
}

export function PhoneInput({
  value,
  onChange,
  autoComplete = 'tel',
  id,
  name,
  disabled,
  className,
  invalid,
  onBlur,
}: PhoneInputProps): JSX.Element {
  const reactId = useId();
  const inputId = id ?? reactId;

  const [digits, setDigits] = useState<string>(() => extractPtDigits(value));

  useEffect(() => {
    setDigits((prev) => (toCanonicalPhone(prev) === value ? prev : extractPtDigits(value)));
  }, [value]);

  const display = formatPtDigits(digits);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const next = extractPtDigits(e.target.value);
    setDigits(next);
    onChange(toCanonicalPhone(next));
  };

  const wrapperClass = [
    styles.wrapper,
    invalid ? styles.invalid : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={wrapperClass}>
      <span className={styles.prefix} aria-hidden>
        <span className={styles.flag}>
          <FlagPT size={18} />
        </span>
        <span className={styles.code}>+351</span>
      </span>
      <input
        id={inputId}
        name={name}
        type="tel"
        inputMode="numeric"
        autoComplete={autoComplete}
        className={styles.input}
        value={display}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder="9XX XXX XXX"
        maxLength={11}
        disabled={disabled}
        aria-invalid={invalid || undefined}
      />
    </span>
  );
}
